import axios from 'axios';

// Ask Clara — analytics assistant grounded in the tenant's CRM data.
// NOTE: this is a separate feature from the legacy "Clara AI" support chatbot
// (see clara-ai.js / /insurance/clara-ai/*). Ask Clara lives under /insurance/clara/*.

const state = {
  open: false, // overlay visibility
  hub: 'SALES', // single hub for /ask ('SALES' | 'SERVICE'), from the current page context
  hubs: ['SALES'], // hubs the current page spans, for /suggestions and /today
  conversationId: null, // null until the first /ask; persists for the tab session
  messages: [], // [{ turnId, role, content, sourcesUsed, feedbackRating, createdAt }]
  suggestionsByHub: {}, // /suggestions byHub: { SALES: [...], SERVICE: [...] }
  insightsByHub: {}, // /today byHub: { SALES: [...], SERVICE: [...] } (empty in MVP)
  loading: false, // true while awaiting /ask
};

const getters = {
  claraOpen: (s) => s.open,
  claraHub: (s) => s.hub,
  claraHubs: (s) => s.hubs,
  claraConversationId: (s) => s.conversationId,
  claraMessages: (s) => s.messages,
  claraSuggestionsByHub: (s) => s.suggestionsByHub,
  claraInsightsByHub: (s) => s.insightsByHub,
  claraLoading: (s) => s.loading,
};

const mutations = {
  SET_OPEN(state, value) {
    state.open = value;
  },
  SET_HUB(state, value) {
    state.hub = value;
  },
  SET_HUBS(state, value) {
    state.hubs = value;
  },
  SET_CONVERSATION_ID(state, value) {
    state.conversationId = value;
  },
  ADD_MESSAGE(state, message) {
    state.messages.push(message);
  },
  SET_MESSAGES(state, messages) {
    state.messages = messages;
  },
  UPDATE_MESSAGE_FEEDBACK(state, { turnId, rating }) {
    const message = state.messages.find((m) => m.turnId === turnId);
    if (message) {
      message.feedbackRating = rating;
    }
  },
  SET_SUGGESTIONS_BY_HUB(state, byHub) {
    state.suggestionsByHub = byHub;
  },
  SET_INSIGHTS_BY_HUB(state, byHub) {
    state.insightsByHub = byHub;
  },
  SET_LOADING(state, value) {
    state.loading = value;
  },
  RESET(state) {
    state.conversationId = null;
    state.messages = [];
    state.loading = false;
  },
};

const actions = {
  // Core endpoint: ask a question / continue the chat.
  // The component pushes the optimistic user bubble and toggles loading so the UI
  // stays responsive; this action does the request and stores the assistant turn.
  async ask({ commit, state: s }, question) {
    const payload = {
      question,
      hub: s.hub,
    };
    if (s.conversationId) {
      payload.conversationId = s.conversationId;
    }
    const { data } = await axios.post('/insurance/clara/ask', payload);
    commit('SET_CONVERSATION_ID', data.conversationId);
    commit('ADD_MESSAGE', {
      turnId: data.turnId,
      role: 'ASSISTANT',
      content: data.answer,
      sourcesUsed: data.sourcesUsed || [],
      feedbackRating: null,
      createdAt: new Date().toISOString(),
    });
    return data;
  },
  // Restore the transcript for display (continuity itself is server-side).
  async loadMessages({ commit, state: s }) {
    if (!s.conversationId) return;
    const { data } = await axios.post('/insurance/clara/messages', {
      conversationId: s.conversationId,
    });
    commit('SET_MESSAGES', data.messages || []);
  },
  async sendFeedback({ commit }, { turnId, rating, comment }) {
    const payload = { turnId, rating };
    if (comment) payload.comment = comment;
    const { data } = await axios.post('/insurance/clara/feedback', payload);
    if (data.ok) {
      commit('UPDATE_MESSAGE_FEEDBACK', { turnId, rating });
    }
    return data;
  },
  async loadSuggestions({ commit }) {
    // Suggestions always span both hubs; the backend returns [] for hubs the
    // account lacks. (/today and /ask still use the page-derived hub context.)
    const { data } = await axios.post('/insurance/clara/suggestions', {
      hubs: ['SALES', 'SERVICE'],
    });
    commit('SET_SUGGESTIONS_BY_HUB', data.byHub || {});
  },
  // Backend gates each hub by subscription + permission, returning [] for hubs the
  // account lacks — so we send every hub the page spans and render whatever comes back.
  async loadToday({ commit, state: s }) {
    const { data } = await axios.post('/insurance/clara/today', { hubs: s.hubs });
    commit('SET_INSIGHTS_BY_HUB', data.byHub || {});
  },
  openPanel({ commit, dispatch, state: s }) {
    commit('SET_OPEN', true);
    dispatch('loadSuggestions');
    dispatch('loadToday');
    // Re-mount with an existing conversation but no loaded messages → rehydrate.
    if (s.conversationId && !s.messages.length) {
      dispatch('loadMessages');
    }
  },
  closePanel({ commit }) {
    commit('SET_OPEN', false);
  },
  // Close via the × button: drop the conversation so the next open starts clean.
  closeAndReset({ commit }) {
    commit('SET_OPEN', false);
    commit('RESET');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
